import React, { useState, useEffect } from 'react';
import { apiService } from '../services/apiService';

const PrimaryRecruiterForm = ({ userInfo }) => {
  const [primaryRecruiter, setPrimaryRecruiter] = useState('');
  const [primaryRecruiterName, setPrimaryRecruiterName] = useState('');
  const [primaryRecruiterLoading, setPrimaryRecruiterLoading] = useState(false);
  const [primaryRecruiterError, setPrimaryRecruiterError] = useState('');
  const [primaryRecruiterSuccess, setPrimaryRecruiterSuccess] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);

  // Load primary recruiter data when component mounts
  useEffect(() => {
    const loadPrimaryRecruiter = async () => {
      if (!userInfo?.tenantId) {
        setInitialLoading(false);
        return;
      }

      try {
        setInitialLoading(true);
        setPrimaryRecruiterError('');
        
        const organizationId = userInfo.tenantId;
        const data = await apiService.getPrimaryRecruiter(organizationId);
        
        // Handle the API response structure - data is an array with the recruiter info in the first element
        if (data && data.data && data.data.length > 0) {
          const recruiterInfo = data.data[0];
          if (recruiterInfo.email && recruiterInfo.name) {
            setPrimaryRecruiter(recruiterInfo.email);
            setPrimaryRecruiterName(recruiterInfo.name);
          }
        } else if (data && data.email && data.name) {
          // Fallback for direct object structure
          setPrimaryRecruiter(data.email);
          setPrimaryRecruiterName(data.name);
        }
        
      } catch (error) {
        console.error('Error loading primary recruiter:', error);
        
        // Only show error if it's not a "not found" error
        if (!error.message.includes('404') && !error.message.includes('not found')) {
          setPrimaryRecruiterError(`Failed to load existing primary recruiter: ${error.message}`);
        }
      } finally {
        setInitialLoading(false);
      }
    };

    loadPrimaryRecruiter();
  }, [userInfo?.tenantId]);

  const savePrimaryRecruiter = async (e) => {
    e.preventDefault();
    setPrimaryRecruiterLoading(true);
    setPrimaryRecruiterError('');
    setPrimaryRecruiterSuccess('');

    if (!primaryRecruiter.trim()) {
      setPrimaryRecruiterError('Primary recruiter email is required');
      setPrimaryRecruiterLoading(false);
      return;
    }

    if (!primaryRecruiterName.trim()) {
      setPrimaryRecruiterError('Primary recruiter name is required');
      setPrimaryRecruiterLoading(false);
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(primaryRecruiter.trim())) {
      setPrimaryRecruiterError('Please enter a valid email address');
      setPrimaryRecruiterLoading(false);
      alert('Invalid email format. Please enter a valid email address.');
      return;
    }

    try {
      // Get organization_id from tenant ID, use fallback if not available
      
      const organizationId = userInfo.tenantId;
      console.log('Using organization ID for primary:', organizationId);

      const recruiterData = {
        name: primaryRecruiterName,
        email: primaryRecruiter,
        organizationId: organizationId
      };

      await apiService.savePrimaryRecruiter(recruiterData);
      console.log('Primary recruiter saved successfully');
      setPrimaryRecruiterSuccess('Primary recruiter saved successfully!');
      
    } catch (error) {
      console.error('Error saving primary recruiter:', error);
      
      // Check for specific error types
      if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
        setPrimaryRecruiterError('Network error: Unable to connect to the server. Please check your internet connection or try again later.');
      } else if (error.message.includes('CORS')) {
        setPrimaryRecruiterError('CORS error: Server configuration issue. Please contact the administrator.');
      } else {
        setPrimaryRecruiterError(`Request failed: ${error.message}`);
      }
      
      // For development/testing - show what would be sent
      const organizationId = userInfo.tenantId;

      const payload = {
        name: primaryRecruiterName.trim(),
        email: primaryRecruiter.trim(),
        organization_id: organizationId,
        is_primary: true,
        is_active: true,
        department: "string"
      };

      console.log('API not available yet, primary recruiter payload would be:', payload);
      setPrimaryRecruiterSuccess('Primary recruiter data prepared (API endpoint not available yet)');
    }

    setPrimaryRecruiterLoading(false);
  };

  return (
    <div className="teams-card">
      <div className="teams-section-header">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <h2 className="teams-section-title">Primary Recruiter</h2>
          <div className="teams-tooltip">
            <div className="teams-tooltip-icon">?</div>
            <div className="teams-tooltip-content">
              Main contact included in all interviews. Only one per organization.
            </div>
          </div>
        </div>
      </div>
      
      <div className="teams-form-group">
        <label htmlFor="primaryRecruiterName" className="teams-label">
          Primary Recruiter Name
        </label>
        <input
          type="text"
          id="primaryRecruiterName"
          value={primaryRecruiterName}
          onChange={(e) => setPrimaryRecruiterName(e.target.value)}
          placeholder={initialLoading ? "Loading..." : "Enter primary recruiter name"}
          className="teams-input"
          disabled={initialLoading}
          required
        />
      </div>
      
      <div className="teams-form-group">
        <label htmlFor="primaryRecruiter" className="teams-label">
          Primary Recruiter Email
        </label>
        <input
          type="email"
          id="primaryRecruiter"
          value={primaryRecruiter}
          onChange={(e) => {
            setPrimaryRecruiter(e.target.value);
            // Clear error when user starts typing again
            if (primaryRecruiterError) {
              setPrimaryRecruiterError('');
            }
          }}
          onBlur={(e) => {
            // Validate email on blur if not empty
            if (e.target.value.trim()) {
              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
              if (!emailRegex.test(e.target.value.trim())) {
                setPrimaryRecruiterError('Please enter a valid email address');
              }
            }
          }}
          placeholder={initialLoading ? "Loading..." : "Enter primary recruiter email"}
          className="teams-input"
          disabled={initialLoading}
          required
        />
      </div>
      
      <button 
        type="button" 
        onClick={savePrimaryRecruiter}
        className="teams-button teams-button-success"
        disabled={primaryRecruiterLoading || initialLoading}
        style={{ width: '100%' }}
      >
        {initialLoading ? 
          <><span className="teams-spinner"></span>Loading...</> :
          primaryRecruiterLoading ? 
          <><span className="teams-spinner"></span>Saving...</> : 
          'Save Primary Recruiter'}
      </button>

      {/* Primary Recruiter Messages */}
      {primaryRecruiterError && (
        <div className="teams-message teams-message-error">
          {primaryRecruiterError}
        </div>
      )}
      {primaryRecruiterSuccess && (
        <div className="teams-message teams-message-success">
          {primaryRecruiterSuccess}
        </div>
      )}
    </div>
  );
};

export default PrimaryRecruiterForm;
